/* eslint-disable @angular-eslint/prefer-inject, @angular-eslint/prefer-standalone -- Credit checkout retains the existing NgModule dialog contract. */
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from '@src/app/core/services/auth.service';
import {BillingCatalogue, BillingOffer, PaymentService} from '@src/app/core/services/payment.service';
import {billingTaxLabel} from '@src/app/shared/helpers/billing-display';

@Component({
	standalone: false,
	selector: 'app-purchase-credits',
	templateUrl: './purchase-credits.component.html',
	styleUrls: ['./purchase-credits.component.scss']
})
export class PurchaseCreditsComponent implements OnInit {
	public offers: BillingOffer[] = [];
	public catalogue: BillingCatalogue | null = null;
	public readonly taxLabel = billingTaxLabel;
	public loading = true;
	public working = false;
	public error: string | null = null;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data,
		public purchaseCreditModal: MatDialogRef<PurchaseCreditsComponent>,
		public paymentService: PaymentService,
		public authService: AuthService,
	) { }

	public ngOnInit(): void {
		this.paymentService.getCatalogue().subscribe({
			next: catalogue => {
				this.catalogue = catalogue;
				this.offers = catalogue.offers
					.filter(offer => offer.kind === 'credit_pack')
					.sort((first, second) => first.amount - second.amount);
				this.loading = false;
			},
			error: response => {
				this.error = response?.error?.message ?? 'Credit packs are temporarily unavailable. Please try again.';
				this.loading = false;
			},
		});
	}

	public closeDialog(response = null): void {
		this.purchaseCreditModal.close(response);
	}

	public discountPercent(offer: BillingOffer): number | null {
		const base = this.offers.find(candidate => candidate.credit_quantity === 250 && candidate.amount > 0
			&& candidate.currency === offer.currency && candidate.tax_behavior === offer.tax_behavior && candidate.plan === offer.plan);
		if (!base || !offer.credit_quantity || offer.amount <= 0) return null;

		const discount = Math.round((1 - (offer.amount / offer.credit_quantity) / (base.amount / base.credit_quantity!)) * 100);
		return discount > 0 ? discount : null;
	}

	public usageExamples(offer: BillingOffer): {label: string; credits: number; amount: number}[] {
		const usage = this.catalogue?.usage;
		if (!usage?.uses_credits || !offer.credit_quantity) return [];
		return [
			{key: 'passive_firestopping', label: 'Passive firestopping'},
			{key: 'fire_door_initial', label: 'Initial fire-door inspection'},
		].flatMap(({key, label}) => {
			const credits = usage.credit_weights?.[key];
			return typeof credits === 'number' && credits > 0
				? [{label, credits, amount: offer.amount * credits / offer.credit_quantity! / 100}]
				: [];
		});
	}

	public checkout(offer: BillingOffer): void {
		this.working = true;
		this.error = null;
		const returnUrl = `${window.location.origin}/app/settings/7`;
		this.paymentService.checkout({
			price_id: offer.price_id,
			success_url: `${returnUrl}?session_id={CHECKOUT_SESSION_ID}`,
			cancel_url: `${returnUrl}?payment_cancelled=true`,
		}).subscribe({
			next: result => {
				if (result?.url) window.location.href = result.url;
				else {
					this.error = 'Secure checkout could not be opened. Please try again.';
					this.working = false;
				}
			},
			error: response => {
				this.error = response?.error?.message ?? 'Secure checkout could not be opened. Please try again.';
				this.working = false;
			},
		});
	}

}
